import React from 'react';
import TrueImpactAnimation from 'shared/design-system/base/TrueImpactAnimation';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import Button from 'shared/design-system/components/TrueImpactButton';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import styled from 'styled-components';
import { GuidanceTextResponse } from '../utils';
import ShowGuidanceText from '../Review/ReportCustomQuestionType/ShowGuidanceText';

const StyledDiv = styled.div`
  margin-top: ${SPACERS.extraLarge};
  margin-bottom: ${SPACERS.extraLarge};
  .section-label {
    margin-top: 40px;
  }
  .heading {
    margin-top: ${SPACERS.small};
    margin-bottom: ${SPACERS.medium};
  }
  .text-wrapper {
    margin-bottom: ${SPACERS.large};
    .sub-heading {
      font-size: 18px;
      line-height: 23.4px;
      letter-spacing: -2%;
      margin-bottom: ${SPACERS.small};
    }
  }
  .youtube-video {
    margin-top: ${SPACERS.small};
  }
  .submit-button {
    min-width: 200px;
    margin-bottom: ${SPACERS.extraLarge};
  }
`;

const QUESTION_ID = 40000;

export const ImpactSummaryIntroBody: React.FC<{ animated?: boolean }> = ({
  animated = false,
}) => {
  const wrap = (
    delay: number,
    className: string | undefined,
    children: React.ReactNode,
  ) =>
    animated ? (
      <TrueImpactAnimation
        isFlex={false}
        animationDelay={delay}
        className={className}
      >
        {children}
      </TrueImpactAnimation>
    ) : (
      <div className={className}>{children}</div>
    );

  return (
    <>
      {wrap(
        3,
        'sub-heading',
        <TrueImpactText variant="body-large-bold">
          In the next two sections, you will:
        </TrueImpactText>,
      )}
      {wrap(
        4,
        undefined,
        <TrueImpactText variant="body">
          <ul>
            <li>
              Build your Impact Model and define your indicators in{' '}
              <strong>Impact Model Builder</strong>
            </li>
            <li>
              Add indicator forecasts or final results and measurement types in{' '}
              <strong>Impact Model Details</strong>
            </li>
            <li>
              Review your model holistically and make any final adjustments in{' '}
              <strong>Impact Model Details</strong>
            </li>
          </ul>
        </TrueImpactText>,
      )}
      {wrap(
        5,
        undefined,
        <TrueImpactText variant="body">
          <a
            target="_blank"
            rel="noreferrer"
            title="How to frame your social impact"
            href="https://support.trueimpact.com/how-to-frame-your-social-impact"
          >
            Learn more True Impact’s Model approach
          </a>
          , or watch this 2 minute overview video:
        </TrueImpactText>,
      )}
      {wrap(
        6,
        'youtube-video',
        <iframe
          width="600"
          height="340"
          allowFullScreen={true}
          style={{ border: 'none' }}
          title="Impact Model Builder and Details"
          src="https://www.youtube.com/embed/rPwrQThbfyM"
          referrerPolicy="strict-origin-when-cross-origin"
          allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        />,
      )}
    </>
  );
};

const ImpactSummaryIntro: React.FC<{
  guidanceText: GuidanceTextResponse;
  handleNext: () => unknown;
  hideBottomBar?: boolean;
}> = ({ guidanceText, handleNext, hideBottomBar }) => {
  return (
    <StyledDiv>
      <TrueImpactAnimation
        isFlex={false}
        variant="appear"
        animationDelay={2}
        className="heading"
      >
        <TrueImpactText variant="heading-page">
          Build Your Impact Model
        </TrueImpactText>
      </TrueImpactAnimation>
      <ShowGuidanceText
        sectionId="impact_summary"
        questionId={QUESTION_ID}
        guidanceText={guidanceText}
      />
      <div className="text-wrapper">
        <ImpactSummaryIntroBody animated={true} />
      </div>
      {!hideBottomBar && (
        <TrueImpactAnimation isFlex={false} animationDelay={7}>
          <Button
            variant="primary-large"
            className="submit-button"
            onClick={handleNext}
          >
            Let's Go!
          </Button>
        </TrueImpactAnimation>
      )}
    </StyledDiv>
  );
};

export default ImpactSummaryIntro;
